import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send, MessageCircle, Clock } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    // Simulate form submission
    setTimeout(() => {
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus(''), 3000);
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen py-16 relative">
      {/* Background decorative elements */}
      <div className="absolute top-40 left-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 right-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      
      <div className="section-container relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-8">
            <MessageCircle size={16} className="text-cyan-400" />
            <span>We're Here to Help</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold font-poppins mb-6">
            Get in <span className="gradient-text">Touch</span>
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed">
            Have questions? Need a custom project? We're here to help!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="glass-card p-8 rounded-2xl border border-cyan-500/10 card-hover">
              <h2 className="text-3xl font-bold mb-8">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0 transform group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-500/30">
                    <Mail className="text-gray-900" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1 text-lg">Email</h3>
                    <a href="mailto:support@scriptsaint.com" className="text-gray-400 hover:text-cyan-400 transition">support@scriptsaint.com</a>
                  </div>
                </div>
                <div className="flex items-start space-x-4 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0 transform group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-500/30">
                    <Phone className="text-gray-900" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1 text-lg">Phone</h3>
                    <a href="tel:+911234567890" className="text-gray-400 hover:text-cyan-400 transition">+91 (123) 456-7890</a>
                  </div>
                </div>
                <div className="flex items-start space-x-4 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0 transform group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-500/30">
                    <MapPin className="text-gray-900" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1 text-lg">Location</h3>
                    <p className="text-gray-400">Bangalore, India</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0 transform group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-500/30">
                    <Clock className="text-gray-900" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1 text-lg">Working Hours</h3>
                    <p className="text-gray-400">Mon - Fri: 9:00 AM - 6:00 PM IST</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-8 rounded-2xl border border-cyan-500/10 card-hover relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl"></div>
              <div className="relative z-10">
                <h2 className="text-2xl font-bold mb-4">Custom Projects</h2>
                <p className="text-gray-400 mb-4 leading-relaxed">
                  Need something tailored to your specific requirements? We build custom solutions from scratch or can configure and deploy any of our prebuilt products for you.
                </p>
                <ul className="space-y-3 text-gray-400">
                  <li className="flex items-center space-x-2">
                    <span className="text-cyan-400 text-xl">✓</span>
                    <span>Full-stack development</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-cyan-400 text-xl">✓</span>
                    <span>White-glove deployment</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-cyan-400 text-xl">✓</span>
                    <span>Ongoing support & maintenance</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="glass-card p-8 md:p-10 rounded-2xl border border-cyan-500/10 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-32 h-32 bg-teal-500/5 rounded-full blur-2xl"></div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-8">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3">
                    Name <span className="text-cyan-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3">
                    Email <span className="text-cyan-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3">
                    Subject <span className="text-cyan-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition"
                    placeholder="What's this about?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3">
                    Message <span className="text-cyan-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition resize-none"
                    placeholder="Tell us more about your project or inquiry..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full gradient-button flex items-center justify-center space-x-2 py-4 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-lg font-semibold shadow-lg shadow-cyan-500/30"
                >
                  {status === 'sending' ? (
                    <>
                      <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Message</span>
                      <Send size={20} />
                    </>
                  )}
                </button>
                {status === 'success' && (
                  <div className="text-center text-green-400 font-semibold bg-green-500/10 border border-green-500/30 py-3 rounded-xl">
                    ✓ Message sent successfully!
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;